/* eslint-disable no-unused-vars */
/* eslint-disable no-plusplus */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import { Alert, Box, LinearProgress, Link, Snackbar } from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from './common/components/LocalizationProvider';
import { nativePostMessage } from './common/components/NativeInterface';
import { useUser } from './common/util/permissions';
import { useAttributePreference } from './common/util/preferences';
import { snackBarDurationLongMs } from './common/util/system_durations';
import useFeatures from './common/util/useFeatures';
import { forgetMe, isMobile } from './common/util/utils';
import { useEffectAsync } from './reactHelper';
import alarm from './resources/alarm.mp3';
import { base64, utf8 } from './resources/libs/self/base64-utf8.module.js';
import { devicesActions, sessionActions } from './store';
import { eventsActions } from './store/events';

const logoutCode = 4000;

function importAll(r) {
  return r.keys().map(r);
}

const sosFiles = import.meta.glob('./resources/alarms/sos/*.{mp3,wav}', { eager: true });
const powerCutFiles = import.meta.glob('./resources/alarms/powerCut/*.{mp3,wav}', { eager: true });

// -----------------------------------------------------------------
// Fetch con reintentos: reemplaza el fetch "desnudo" que si fallaba
// una vez dejaba al usuario sin devices y sin socket para siempre.
// -----------------------------------------------------------------
async function fetchDevicesWithRetry(maxRetries = 3) {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch('/api/devices');
      if (response.ok) {
        const data = await response.json();

        // Instrumentación: si la API respondió OK pero vino vacía,
        // lo queremos saber (puede ser legítimo, pero lo logueamos con contexto).
        if (!data || data.length === 0) {
          // Sentry.captureMessage('devices fetch OK pero vacío', {
          //   level: 'warning',
          //   extra: { attempt, isMobile: isMobile(), userAgent: navigator.userAgent },
          // });
        }
        return data;
      }

      lastError = new Error(`devices fetch status ${response.status}`);
      // Sentry.captureMessage('devices fetch no-ok', {
      //   level: 'warning',
      //   extra: {
      //     attempt,
      //     status: response.status,
      //     statusText: response.statusText,
      //     isMobile: isMobile(),
      //     userAgent: navigator.userAgent,
      //   },
      // });
    } catch (error) {
      lastError = error;
      // Sentry.captureException(error, {
      //   extra: { attempt, context: 'fetchDevicesWithRetry', isMobile: isMobile() },
      // });
    }

    // backoff simple antes de reintentar (500ms, 1000ms, 1500ms...)
    if (attempt < maxRetries) {
      await new Promise((resolve) => { setTimeout(resolve, attempt * 500); });
    }
  }

  // Si llegamos acá, todos los intentos fallaron.
  // Sentry.captureException(lastError, {
  //   level: 'error',
  //   extra: { context: 'fetchDevicesWithRetry - todos los reintentos fallaron' },
  // });
  throw lastError;
}

const logout = async () => {
  forgetMe();
  const notificationToken = window.localStorage.getItem('notificationToken');
  if (notificationToken && !user.readonly) {
    window.localStorage.removeItem('notificationToken');
    const tokens = user.attributes.notificationTokens?.split(',') || [];
    if (tokens.includes(notificationToken)) {
      const updatedUser = {
        ...user,
        attributes: {
          ...user.attributes,
          notificationTokens: tokens.length > 1 ? tokens.filter((it) => it !== notificationToken).join(',') : undefined,
        },
      };
      await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedUser),
      });
    }
  }
  localStorage.removeItem(base64.encode('bypassForcedLogout'));
  await fetch('/api/session', { method: 'DELETE' });
  sessionStorage.removeItem('offlineDevicesAlertShown');
  nativePostMessage('logout');
  navigate('/login');
  dispatch(sessionActions.updateUser(null));
}

function parseBoolean(value) {
  if (typeof value !== 'string') return false;
  return value.trim().toLowerCase() === 'true';
}

const SocketController = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useTranslation();
  const location = useLocation();

  const authenticated = useSelector((state) => !!state.session.user);
  const user = useUser();
  const devices = useSelector((state) => state.devices.items);

  const socketRef = useRef();
  const intervalsRef = useRef([]); // para limpiar intervals en cada reconexión

  const [events, setEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const soundEvents = useAttributePreference('soundEvents', '');
  const soundAlarms = useAttributePreference('soundAlarms', 'sos');

  const features = useFeatures();

  const [forceLogoutMessage, setForceLogoutMessage] = useState(null);

  const allowedFunctions = {
    refreshUser: async () => {
      const response = await fetch('/api/session');
      if (response.ok) {
        dispatch(sessionActions.updateUser(await response.json()));
      }
    },
    reload: async () => {
      window.location.reload();
    },
    logout: () => {
      logout();
    },
    forceLogout: (customObj) => {

      const bypassForcedLogout = parseBoolean(
        localStorage.getItem(base64.encode('bypassForcedLogout'))
      );


      console.log({ bypassForcedLogout, localstorage: localStorage.getItem(base64.encode('bypassForcedLogout')) });

      if (!bypassForcedLogout) {
        setForceLogoutMessage(customObj.message || t('sessionExpired'));
        setTimeout(() => {
          logout();
        }, 5000);
      }
    },
  };

  const connectSocket = () => {
    try {
      // Limpiar intervals de una conexión anterior (evita duplicados en reconexiones)
      intervalsRef.current.forEach(clearInterval);
      intervalsRef.current = [];

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${protocol}//${window.location.host}/api/socket`);
      socketRef.current = socket;
      window.traccarSocket = socket;

      const callCounters = {
        updateDevices: 0,
        updatePositions: 0,
        addEvents: 0,
      };

      const trackDispatch = (actionType) => {
        callCounters[actionType]++;
      };

      const counterInterval = setInterval(() => {
        callCounters.updateDevices = 0;
        callCounters.updatePositions = 0;
        callCounters.addEvents = 0;
      }, 1000);
      intervalsRef.current.push(counterInterval);

      const positionQueue = [];
      // Cola de mensajes 'devices' recibidos mientras document.hidden era true,
      // para no perderlos (antes se descartaban directo).
      const pendingHiddenDeviceUpdates = [];

      const queuePositions = (positions) => {
        positionQueue.push(...positions);
      };

      const flushInterval = setInterval(() => {
        if (positionQueue.length > 0) {
          trackDispatch('updatePositions');
          dispatch(sessionActions.updatePositions(positionQueue));
          positionQueue.length = 0;
        }
      }, 1000);
      intervalsRef.current.push(flushInterval);

      socket.onopen = () => {
        dispatch(sessionActions.updateSocket(true));
        // Sentry.addBreadcrumb({ category: 'socket', message: 'socket onopen', level: 'info' });
      };

      socket.onerror = (error) => {
        // Antes no había ningún manejo de error del socket.
        // Sentry.captureMessage('websocket onerror', {
        //   level: 'error',
        //   extra: { isMobile: isMobile(), userAgent: navigator.userAgent },
        // });
      };

      socket.onclose = async (event) => {
        dispatch(sessionActions.updateSocket(false));
        // Sentry.addBreadcrumb({
        //   category: 'socket',
        //   message: `socket onclose code=${event.code}`,
        //   level: 'info',
        // });
        if (event.code !== logoutCode) {
          try {
            const devicesResponse = await fetch('/api/devices');
            if (devicesResponse.ok) {
              dispatch(devicesActions.update(await devicesResponse.json()));
            }
            const positionsResponse = await fetch('/api/positions');
            if (positionsResponse.ok) {
              dispatch(sessionActions.updatePositions(await positionsResponse.json()));
            }
            if (devicesResponse.status === 401 || positionsResponse.status === 401) {
              navigate('/login');
            }
          } catch (error) {
            // Sentry.captureException(error, { extra: { context: 'socket.onclose refetch' } });
          }
          setTimeout(() => connectSocket(), 60000);
        }
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.devices) {
          if (document.hidden) {
            // Antes esto se perdía para siempre. Ahora lo guardamos
            // y lo instrumentamos para saber cuánto pasa esto en la práctica.
            pendingHiddenDeviceUpdates.push(data.devices);
            // Sentry.addBreadcrumb({
            //   category: 'socket',
            //   message: 'devices update recibido con document.hidden=true, encolado',
            //   level: 'info',
            // });
          } else {
            trackDispatch('updateDevices');
            dispatch(devicesActions.update(data.devices));
          }
        }
        if (data.positions) {
          if (!document.hidden) {
            queuePositions(data.positions);
          }
        }
        if (data.events) {
          if (!document.hidden) {
            if (!features.disableEvents) {
              trackDispatch('addEvents');
              dispatch(eventsActions.add(data.events));
            }
            setEvents(data.events);
          }
        }
        if (data.custom) {
          const customObj = JSON.parse(data.custom);
          (allowedFunctions[customObj.command] ?? (() => { }))(customObj);
        }
      };

      // Cuando la pestaña vuelve a estar visible, aplicamos lo que quedó pendiente.
      const visibilityHandler = () => {
        if (!document.hidden && pendingHiddenDeviceUpdates.length > 0) {
          pendingHiddenDeviceUpdates.forEach((devicesUpdate) => {
            dispatch(devicesActions.update(devicesUpdate));
          });
          // Sentry.addBreadcrumb({
          //   category: 'socket',
          //   message: `aplicando ${pendingHiddenDeviceUpdates.length} devices update(s) pendientes tras volver a visible`,
          //   level: 'info',
          // });
          pendingHiddenDeviceUpdates.length = 0;
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
      socketRef.current._visibilityHandler = visibilityHandler;
    } catch (error) {
      // Sentry.captureException(error, { extra: { context: 'connectSocket' } });
      console.error(error);
    }
  };

  useEffectAsync(async () => {
    if (authenticated) {
      console.log({ authenticated, user })
      if (user.administrator) {
        localStorage.setItem(base64.encode('bypassForcedLogout'), user.administrator);
      }
      try {
        const data = await fetchDevicesWithRetry();
        dispatch(devicesActions.refresh(data));
        connectSocket();
      } catch (error) {
        // Antes: esto quedaba como unhandled rejection y el usuario
        // se quedaba sin devices y sin socket, sin ningún aviso.
        // Sentry.captureException(error, {
        //   level: 'error',
        //   extra: { context: 'carga inicial de devices fallo definitivamente' },
        // });
        // Fallback: igual intentamos conectar el socket, para no dejar
        // al usuario completamente colgado si el fetch fallaba pero el
        // socket sí puede levantar.
        connectSocket();
      }

      window.reconnectSocket = async () => {
        const socket = socketRef.current;
        if (socket) {
          await socket.close();
          connectSocket();
        }
      };

      return () => {
        const socket = socketRef.current;
        if (socket) {
          if (socket._visibilityHandler) {
            document.removeEventListener('visibilitychange', socket._visibilityHandler);
          }
          socket.close(logoutCode);
        }
        intervalsRef.current.forEach(clearInterval);
        intervalsRef.current = [];
      };
    }
    return null;
  }, [authenticated]);

  useEffect(() => {
    if (!isMobile() && location.pathname == '/') {
      setNotifications(events.map((event) => ({
        id: event.id,
        message: event.attributes.message,
        show: true,
        position: { vertical: 'bottom', horizontal: 'right' }
      })));
    }
  }, [events, devices, t]);

  useEffect(() => {
    events.forEach((event) => {
      if (soundEvents.includes(event.type) || (event.type === 'alarm' && soundAlarms.includes(event.attributes.alarm))) {
        let audioFile = alarm;

        if (event.attributes.alarm) {
          switch (event.attributes.alarm) {
            case 'sos':
              audioFile = sosFiles[Math.floor(Math.random() * sosFiles.length)];
              break;
            case 'powerCut':
              audioFile = powerCutFiles[Math.floor(Math.random() * powerCutFiles.length)];
              break;
            default: break;
          }
        }

        new Audio(audioFile).play();
      }
    });
  }, [events, soundEvents, soundAlarms]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setNotifications([]);
    }
  }, [location.pathname]);

  return (
    <>
      {forceLogoutMessage && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={!!forceLogoutMessage}
        >
          <Alert severity="warning" variant="filled">
            {forceLogoutMessage}
          </Alert>
        </Snackbar>
      )}
      {notifications.map((notification) => (
        <Snackbar
          anchorOrigin={notification.position}
          key={notification.id}
          open={notification.show}
          autoHideDuration={snackBarDurationLongMs}
          onClose={() => setEvents(events.filter((e) => e.id !== notification.id))}
        >
          <Alert
            severity="info"
            onClose={() => setEvents(events.filter((e) => e.id !== notification.id))}
            variant="filled"
            sx={{
              fontSize: '12px',
              maxHeight: '20dvh',
              maxWidth: '90dvw',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: 'black',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              transition: 'background-color 0.3s, opacity 0.3s',
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.0,
                backdropFilter: 'none',
              },
            }}
          >
            {notification.message.split('\\n').map((line, i) => (
              <React.Fragment key={i}>
                {line.includes('http') ? <Link href={line} target='_blank'>{line}</Link> : line}
                <br />
              </React.Fragment>
            ))}
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '3px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'rgba(9, 12, 155, 0.9)',
                  transformOrigin: 'left',
                  animation: `shrink ${snackBarDurationLongMs}ms linear forwards`,
                },
                '@keyframes shrink': {
                  from: { transform: 'scaleX(1)' },
                  to: { transform: 'scaleX(0)' },
                },
              }}
            />
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};

export default connect()(SocketController);